import { UserPreferencesState } from '@hooks/UserPreferences';
import {
  IS_CYPRESS,
  KEY_USER_PREFERENCES,
  KEY_USER_SETTINGS,
  LINK_OPENING_NEW_TAB,
} from '@utils/constants';
import { getLocalStorage } from '@utils/localStorage';
import { win } from '@utils/win';

// ts-unused-exports:disable-next-line
export interface OpenUrlOptions {
  /** Force new window regardless of user preference (e.g., popup comparison views) */
  forceWindow?: boolean;
  /** Window features string for when window mode is used */
  features?: string;
  /** Target name (defaults to '_blank') */
  target?: string;
}

interface CachedUserSettings {
  getUserPreferences?: { linkOpeningPreference?: string | null };
}

/**
 * Whether the user prefers links to open in a new tab instead of a new window.
 *
 * Reads the backend-persisted "Link Opening Preference" (ME-473813) from the
 * cached user settings, falling back to the legacy `newWindowOptOut` flag for
 * users who set the now-removed debug toggle.
 */
export const shouldOpenLinksInNewTab = (): boolean => {
  const linkOpeningPreference =
    getLocalStorage<CachedUserSettings>(KEY_USER_SETTINGS)?.getUserPreferences
      ?.linkOpeningPreference;

  if (linkOpeningPreference) {
    return linkOpeningPreference === LINK_OPENING_NEW_TAB;
  }

  return (
    getLocalStorage<UserPreferencesState>(KEY_USER_PREFERENCES)
      ?.newWindowOptOut ?? false
  );
};

/**
 * Opens a URL respecting the user's "Open links in new tabs instead of new windows" preference.
 *
 * - When the preference is enabled (or in Cypress), opens as a new tab (no window features).
 * - When the preference is disabled, opens as a new window with the provided features.
 * - Use `forceWindow: true` to bypass the preference (e.g., for popup comparison views).
 */
export const openUrl = (
  url: string,
  options?: OpenUrlOptions,
): Window | null => {
  const { forceWindow, features, target = '_blank' } = options ?? {};

  if (IS_CYPRESS) {
    return win.open(url, target);
  }

  const shouldOpenAsTab = !forceWindow && shouldOpenLinksInNewTab();

  if (shouldOpenAsTab) {
    return win.open(url, target, 'noopener,noreferrer');
  }

  return win.open(url, target, features);
};
